import React, { useState, useRef, useCallback, useEffect } from 'react';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import OrderCard from './components/OrderCard';
import NotFound from './components/NotFound';
import FeedbackBar from './components/FeedbackBar';
import QueueCard from './components/QueueCard';
import ChatHeader from './components/ChatHeader';
import HoneywellBanner from './components/HoneywellBanner';
import LiveAgentDiagnostics from './components/LiveAgentDiagnostics';
import { useChat } from './hooks/useChat';
import { useAutoScroll } from './hooks/useAutoScroll';
import { useDirectLine } from './hooks/useDirectLine';
import { fetchUsername } from './api/chatApi';
import { BACKEND_BASE_URL } from './api/config';
import { checkSfdcUser } from './api/sfdcApi';
import { isChatbotAccessAllowed } from './utils/registrationRules';
import { LOCAL_SESSION_KEY, LOCAL_UID_KEY } from './utils/sessionKeys';
import HTLogoRed from './assets/HTLogoRed.svg';
import loadingGPTIcon from './assets/loadingGPTIcon 1.svg';

const REGISTRATION_URL = 'https://buildingsbt.stage.honeywell.com/us/en/home/my-hbt/register';

const config = {
  scope: 'openid+email+profile',
  client_id: 'Client_9h7c5bdccewd',
  ssoUrl: 'https://qauthn.honeywell.com/as/authorization.oauth2',
  redirect_uri: 'https://emailchat.staging.honeywell.com',
  responseType: 'code',
};

const LOCAL_DEV_ORIGIN = 'http://localhost:3000';
const TOKEN_EXCHANGE_IN_PROGRESS_KEY = 'token_exchange_in_progress_code';
const TOKEN_EXCHANGE_DONE_KEY = 'token_exchange_done_code';

const isLocalDevHost = (hostname) => hostname === 'localhost' || hostname === '127.0.0.1';

const buildAuthState = (payload) => {
  try {
    // URL-safe base64 keeps state opaque and avoids '+'/'/' issues in query handling.
    return btoa(JSON.stringify(payload)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch (error) {
    console.error('Failed to encode auth state:', error);
    return '';
  }
};

const parseAuthState = (encodedState) => {
  if (!encodedState) return null;

  try {
    const normalized = encodedState
      .replace(/ /g, '+')
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padLength = (4 - (normalized.length % 4)) % 4;
    const padded = normalized + '='.repeat(padLength);
    return JSON.parse(atob(padded));
  } catch (error) {
    console.error('Failed to decode auth state:', error);
    return null;
  }
};

const buildLocalUserchatUrl = (basePath = '/userchat') => {
  const url = new URL(basePath, window.location.origin);
  const queryParams = new URLSearchParams(window.location.search);

  const session = queryParams.get('session') || sessionStorage.getItem(LOCAL_SESSION_KEY);
  const uid = queryParams.get('uid') || sessionStorage.getItem(LOCAL_UID_KEY);

  if (session) {
    url.searchParams.set('session', session);
  }

  if (uid) {
    url.searchParams.set('uid', uid);
  }

  return url;
};

function ChatPage() {
  const {
    messages, order, orderLoading, userName, userNameLoading, sessionEnded,
    liveAgentConnectivity, isLiveAgent, escalationRequired, clearEscalation,
    clearLiveAgentConnectivity, sessionTimedOut, sessionId, uuid,
  } = useChat();
  const showLiveAgentDiagnostics = new URLSearchParams(window.location.search).get('debugLiveAgent') === '1';

  // True once the REST-based live-agent handoff (switchToLiveAgent or an
  // auto-detected live_agent_connectivity session) is active.
  const isLiveAgentActive = isLiveAgent || liveAgentConnectivity;

  const {
    dlMessages, directLineLoading, isLiveMode, dlOnline, dlHandoff, dlFailed,
    dlQueueActive, dlWaitTime, dlExiting, attachmentUploading, liveAgentDiagnostics,
    canAttachFile, handleSend, handleRetry, handleAttachFile, retryLiveAgent,
    connectToLiveAgent, endLiveAgentChat,
  } = useDirectLine({
    orderLoading, order, userName, userNameLoading, liveAgentConnectivity,
    escalationRequired, clearEscalation, clearLiveAgentConnectivity, sessionTimedOut,
  });

  const chatEndRef = useAutoScroll([messages, dlMessages, directLineLoading]);

  // Merge messages by time so a /question sent after exiting live chat renders
  // after the live-chat history instead of always appearing before it.
  const allMessages = [...messages, ...dlMessages].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  return (
    <div className="app">
      <HoneywellBanner />

      <ChatHeader
        dlFailed={dlFailed}
        isLiveMode={isLiveMode}
        dlExiting={dlExiting}
        dlOnline={dlOnline}
        userNameLoading={userNameLoading}
        isLiveAgentActive={isLiveAgentActive}
        showLiveAgentLabel={isLiveAgent || dlHandoff}
        onRetry={retryLiveAgent}
        onExit={() => endLiveAgentChat()}
        onConnect={connectToLiveAgent}
      />

      {sessionTimedOut && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="session-timeout-title" style={{ background: '#fff', borderRadius: 16, padding: '20px', maxWidth: 450, width: '90%' }}>
            <h2 id="session-timeout-title" style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600, textAlign: 'center' }}>Session ended</h2>
            <p style={{ fontSize: 14, textAlign: 'center', margin: '0 0 10px' }}>You have been idle for 30 minutes, so your session has ended.</p>
            <p style={{ fontSize: 14, textAlign: 'center', margin: '0 0 20px' }}>To Restart the session, click the button below.</p>
            <button type="button" className="session-restart-btn" onClick={() => window.location.reload()}>Yes, Restart</button>
          </div>
        </div>
      )}

      {showLiveAgentDiagnostics && <LiveAgentDiagnostics diagnostics={liveAgentDiagnostics} />}

      <div className="main-content">
        <p className="session-disclaimer">
          Chat sessions use AI and are recorded for quality and training purposes. This is a global service so your personal data may be transferred to a location outside of your country. Honeywell takes measures to keep your personal data safe which includes implementing a valid data transfer mechanism such as the EU Standard Contractual Clauses. By continuing to chat, you agree to the <a href="https://www.honeywell.com/us/en/legal/privacy-statement" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'blue' }}>Honeywell Privacy Statement</a>.
        </p>
        <div className="greeting">
          <h1>Hi there, {userName ? userName.split('@')[0].split('.')[0].replace(/^./, c => c.toUpperCase()) : '…'}.</h1>
          {order?.order_number && order.order_number.trim() !== '' && <b>Let's get the information we have from your email.</b>}
        </div>

        {!order?.message && order?.order_number && order.order_number.trim() !== '' && <p className="section-note">
          Here are the details of the order we'll be working on…
        </p>}

        {orderLoading ? (
          <div className="order-loading">
            <p>Loading order details</p>
            <div className="spinner"></div>
          </div>
        ) : (
          !order?.message && order?.order_number && order.order_number.trim() !== '' && <OrderCard order={order} />
        )}

        <div className="chat-area">
          {allMessages.map((msg, i) => (
            <ChatMessage key={i} msg={msg} onRetry={handleRetry} onRetryLiveAgent={retryLiveAgent} />
          ))}
          {directLineLoading && !isLiveAgentActive && (
            <div className="response-loader">
              <img src={loadingGPTIcon} alt="" className="response-loader-icon" />
              {/* <span className="response-loader-text">Searching our support center</span> */}
              <span className="response-loader-dots"><span /><span /><span /><span /></span>
            </div>
          )}
          {dlQueueActive && !dlHandoff && !dlFailed && (
            <QueueCard waitTime={dlWaitTime} onExit={() => endLiveAgentChat()} exiting={dlExiting} />
          )}
          <div ref={chatEndRef} />
        </div>

        {sessionEnded && !isLiveAgentActive && <FeedbackBar sessionId={sessionId} uuid={uuid} />}
      </div>

      <div className="bottom-bar">
        <ChatInput
          onSend={handleSend}
          disabled={!dlOnline || userNameLoading || dlExiting || (isLiveMode && !dlHandoff) || sessionTimedOut}
          onAttachFile={canAttachFile ? handleAttachFile : undefined}
          attachDisabled={attachmentUploading}
        />
      </div>
    </div>
  );
}

export default function App() {
  // Registration and SSO logic disabled for testing — both gates bypassed below.
  const [checkingRegistration, setCheckingRegistration] = useState(false);
  const [isRegistered, setIsRegistered] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const registrationCheckedRef = useRef(false);

  const fetchToken = useCallback(async (authCode) => {
    if (!authCode) return;

    const inProgressCode = sessionStorage.getItem(TOKEN_EXCHANGE_IN_PROGRESS_KEY);
    const doneCode = sessionStorage.getItem(TOKEN_EXCHANGE_DONE_KEY);
    const hasToken = !!sessionStorage.getItem('token');

    // Guard against duplicate exchanges for the same authorization code
    // (e.g. re-renders, effect re-runs, or the user navigating back).
    if (inProgressCode === authCode || (doneCode === authCode && hasToken)) {
      return;
    }

    sessionStorage.setItem(TOKEN_EXCHANGE_IN_PROGRESS_KEY, authCode);

    try {
      // Token exchange must happen server-side to avoid browser CORS blocks and secret exposure.
      const response = await fetch(`${BACKEND_BASE_URL}/api/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          redirect_uri: config.redirect_uri,
          client_id: config.client_id,
          grant_type: 'authorization_code',
        }),
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data?.is_token_valid) {
        console.error('Backend reported invalid token');
        sessionStorage.removeItem(TOKEN_EXCHANGE_IN_PROGRESS_KEY);
        return;
      }

      if (data.id_token) {
        sessionStorage.setItem('id_token', data.id_token);
      }

      if (data.token_validation) {
        sessionStorage.setItem('token_data', JSON.stringify(data));
      }

      if (data.access_token) {
        sessionStorage.setItem('token', data.access_token);
        sessionStorage.setItem(TOKEN_EXCHANGE_DONE_KEY, authCode);
        sessionStorage.removeItem(TOKEN_EXCHANGE_IN_PROGRESS_KEY);
        // redirect_uri has no path, so after the OAuth round-trip window.location.pathname
        // is always '/' — send the user back to /userchat with session/uid restored.
        window.location.href = buildLocalUserchatUrl('/userchat').toString();
      } else {
        sessionStorage.removeItem(TOKEN_EXCHANGE_IN_PROGRESS_KEY);
        console.error('Token was not returned by backend exchange endpoint');
      }
    } catch (error) {
      sessionStorage.removeItem(TOKEN_EXCHANGE_IN_PROGRESS_KEY);
      console.error('Error fetching token:', error);
    }
  }, []);

  const initSSO = useCallback(() => {
    if (!config.client_id || !config.ssoUrl || !config.redirect_uri) {
      console.error('Missing SSO configuration values');
      return;
    }

    const isLocalDev = isLocalDevHost(window.location.hostname);
    const queryParams = new URLSearchParams(window.location.search);
    const session = queryParams.get('session');
    const uid = queryParams.get('uid');
    const authCode = queryParams.get('code');
    const encodedState = queryParams.get('state');
    const parsedState = parseAuthState(encodedState);
    const token = sessionStorage.getItem('token');
    const localQuery = window.location.search;

    // Persist session/uid so they survive the SSO redirect round-trip, even on
    // the deployed (non-localhost) host where redirect_uri strips them from the URL.
    if (session) {
      sessionStorage.setItem(LOCAL_SESSION_KEY, session);
    }
    if (uid) {
      sessionStorage.setItem(LOCAL_UID_KEY, uid);
    }

    // Token already present — just make sure session/uid survive on the URL.
    if (token) {
      if (!session || !uid) {
        window.location.href = buildLocalUserchatUrl('/userchat').toString();
        return;
      }
      setIsAuthenticated(true);
      return;
    }

    if (authCode) {
      // If auth callback lands on non-localhost in a local-dev flow, forward code back to localhost.
      if (!isLocalDev && parsedState?.localDevRedirect) {
        const localRedirectPath = parsedState.localPath || '/userchat';
        const localRedirectUrl = new URL(localRedirectPath, LOCAL_DEV_ORIGIN);
        const localQueryParams = new URLSearchParams(parsedState?.localQuery || '');

        const redirectedSession = parsedState?.session || localQueryParams.get('session') || sessionStorage.getItem(LOCAL_SESSION_KEY);
        const redirectedUid = parsedState?.uid || localQueryParams.get('uid') || sessionStorage.getItem(LOCAL_UID_KEY);

        if (redirectedSession) {
          localRedirectUrl.searchParams.set('session', redirectedSession);
        }

        if (redirectedUid) {
          localRedirectUrl.searchParams.set('uid', redirectedUid);
        }

        localRedirectUrl.searchParams.set('code', authCode);

        if (encodedState) {
          localRedirectUrl.searchParams.set('state', encodedState);
        }

        window.location.href = localRedirectUrl.toString();
        return;
      }

      fetchToken(authCode);
      return;
    }

    const effectiveSession = session || sessionStorage.getItem(LOCAL_SESSION_KEY) || null;
    const effectiveUid = uid || sessionStorage.getItem(LOCAL_UID_KEY) || null;

    const statePayload = buildAuthState({
      localDevRedirect: isLocalDev,
      localPath: window.location.pathname,
      session: effectiveSession,
      uid: effectiveUid,
      localQuery,
    });
    const stateQuery = statePayload ? `&state=${encodeURIComponent(statePayload)}` : '';
    const url = `${config.ssoUrl}?client_id=${config.client_id}&scope=${config.scope}&response_type=${config.responseType}&redirect_uri=${config.redirect_uri}${stateQuery}`;

    window.location.href = url;
  }, [fetchToken]);

  // Verify the customer is a registered HBT user before starting the SSO/token
  // flow. Unregistered users are redirected to the registration page instead.
  const checkRegistration = useCallback(async () => {
    if (registrationCheckedRef.current) return;
    registrationCheckedRef.current = true;

    const isValidRoute = window.location.pathname.endsWith('/userchat');

    // Already authenticated earlier in this session — registration was verified then.
    if (!isValidRoute || sessionStorage.getItem('token')) {
      setIsRegistered(true);
      setCheckingRegistration(false);
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const sessionId = queryParams.get('session') || sessionStorage.getItem(LOCAL_SESSION_KEY);
    const uid = queryParams.get('uid') || sessionStorage.getItem(LOCAL_UID_KEY);

    // Can't verify registration without session/uid (e.g. mid-OAuth callback); continue as-is.
    if (!sessionId || !uid) {
      setIsRegistered(true);
      setCheckingRegistration(false);
      return;
    }

    try {
      const userData = await fetchUsername(sessionId, uid);
      const email = userData?.customerEmailId || '';

      if (email) {
        const sfdcResponse = await checkSfdcUser(email);

        if (!isChatbotAccessAllowed(sfdcResponse)) {
          setIsRegistered(false);
          setCheckingRegistration(false);
          return; // show the "not registered" screen instead of the chatbot
        }
      }
    } catch (error) {
      console.error('Failed to verify registration status:', error);
      // Fail open — don't block chatbot access on a lookup error.
    }

    setIsRegistered(true);
    setCheckingRegistration(false);
  }, []);

  // Auth flow: verify registration first, then run the SSO/token flow.
  // Both disabled for testing — see initial state overrides above.
  // useEffect(() => {
  //   checkRegistration();
  // }, [checkRegistration]);

  // useEffect(() => {
  //   if (!checkingRegistration && isRegistered) {
  //     initSSO();
  //   }
  // }, [checkingRegistration, isRegistered, initSSO]);

  // The OAuth redirect_uri has no path, so the callback (?code=...&state=...) always
  // lands on '/'. Treat that as part of the valid app flow too, so we show the
  // "Signing you in…" spinner instead of briefly flashing the 404 page while the
  // authorization code is exchanged for a token before redirecting to /userchat.
  const hasAuthCode = new URLSearchParams(window.location.search).has('code');
  const isValidRoute = window.location.pathname.endsWith('/userchat') || window.location.pathname === '/' || hasAuthCode;

  if (isValidRoute && !checkingRegistration && !isRegistered) {
    return (
      <div className="app-loading">
        <div className="registration-card">
          <img src={HTLogoRed} alt="Honeywell" className="registration-logo" />
          <div className="registration-icon" aria-hidden="true">!</div>
          <h2 className="registration-title">*User Not Registered*</h2>
          {/* <p className="registration-message">
            Register using the link below.
          </p> */}
          <div className="registration-actions">
            <button
              className="registration-btn"
              onClick={() => { window.open(REGISTRATION_URL, '_blank', 'noopener,noreferrer'); }}
            >
              Register now
            </button>
            <button
              className="registration-btn registration-btn-secondary"
              onClick={() => {
                // Reload with session/uid preserved (from the URL or sessionStorage) so the
                // registration re-check and SSO flow run again against the same customer.
                window.location.href = buildLocalUserchatUrl(window.location.pathname).toString();
              }}
            >
              Sign in
            </button>
          </div>
          <p className="registration-note">
            Please ensure you complete the registration process before attempting to sign in again.
          </p>
        </div>
      </div>
    );
  }

  // Keep the loading spinner up until we've both confirmed registration and
  // obtained a valid token — otherwise the chatbot can briefly flash before
  // the SSO redirect/token exchange completes.
  if (isValidRoute && !(isAuthenticated && isRegistered)) {
    return (
      <div className="app-loading">
        <p>{checkingRegistration ? 'Checking your account…' : 'Signing you in…'}</p>
        <div className="spinner"></div>
      </div>
    );
  }

  return isValidRoute ? <ChatPage /> : <NotFound />;
}
